import { useCallback, useEffect } from "react";
import PropTypes from "prop-types";

import { dispatch } from "../../../../../../state/store";
import { resetReleaseNotesList } from "../../../../../../state/ducks";

import {
  alignItems,
  crossPageNotifications,
  releaseNoteOptions,
  routePaths,
  strCode
} from "../../../../../../consts";
import { log, t } from "../../../../../../services";

import { FlexContainers, IconButton, SVG, WrapperContainer } from "../../../../../../components";
import { SimpleListPreview } from "./components/SimpleListPreview/SimpleListPreview";
import { ListWithIconsPreview } from "./components/ListWithIconsPreview/ListWithIconsPreview";
import { WalkthroughPreview } from "./components/WalkthroughPreview/WalkthroughPreview";
import { PreviewContainer, Label, IconContainer } from "./styled";

export const ReleaseNotePreview = ({
  version,
  releaseNotesType,
  releaseNotes,
  mediaData,
  thumbnailsData,
  onFetchReleaseNotes
}) => {
  const onEdit = () => {
    window.open(`${window.location.origin}${routePaths.FW_VERSION_EDIT}/${version}`, "_blank");
  };

  const onRefresh = () => {
    dispatch(resetReleaseNotesList());
    onFetchReleaseNotes(version);
  };

  const onMessage = useCallback(
    (event) => {
      if (event.origin !== window.location.origin) return;

      const data = event.data;
      const updatedVerion = data?.payload;

      if (data?.type === crossPageNotifications.releaseNotesUpdate) {
        log.trace("MessagePreview: Received message from child page " + JSON.stringify(data));
      }

      if (data?.type === crossPageNotifications.releaseNotesUpdate && version === updatedVerion) {
        onRefresh();
      }
    },
    [version]
  );

  useEffect(() => {
    window.addEventListener("message", onMessage);

    return () => {
      window.removeEventListener("message", onMessage);
    };
  }, [onMessage]);

  return (
    <WrapperContainer
      Container={FlexContainers.FlexColumnStart}
      alignItems={alignItems.start}
      width={"100%"}
      gap={4}>
      <Label>{t(strCode.RELEASE_NOTES_PREVIEW_TITLE)}</Label>
      <PreviewContainer Container={FlexContainers.FlexColumnStart} width={"100%"}>
        {releaseNotesType === releaseNoteOptions.SIMPLE_LIST && (
          <SimpleListPreview releaseNotes={releaseNotes} />
        )}

        {releaseNotesType === releaseNoteOptions.LIST_WITH_ICONS && (
          <ListWithIconsPreview
            version={version}
            releaseNotes={releaseNotes}
            mediaData={mediaData}
            thumbnailsData={thumbnailsData}
          />
        )}

        {releaseNotesType === releaseNoteOptions.WALKTHROUGH && (
          <WalkthroughPreview releaseNotes={releaseNotes} mediaData={mediaData} />
        )}

        {version && (
          <IconContainer Container={FlexContainers.FlexRowStart}>
            <IconButton hasHoverCircle onClick={onEdit}>
              <SVG.EditIcon />
            </IconButton>
            <IconButton hasHoverCircle onClick={onRefresh}>
              <SVG.RefreshIcon />
            </IconButton>
          </IconContainer>
        )}
      </PreviewContainer>
    </WrapperContainer>
  );
};

ReleaseNotePreview.propTypes = {
  version: PropTypes.string.isRequired,
  releaseNotesType: PropTypes.string.isRequired,
  releaseNotes: PropTypes.object.isRequired,
  mediaData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      imageFile: PropTypes.object.isRequired,
      mediaExtension: PropTypes.string.isRequired
    })
  ),
  thumbnailsData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      imageFile: PropTypes.object.isRequired,
      mediaExtension: PropTypes.string.isRequired
    })
  ),
  onFetchReleaseNotes: PropTypes.func.isRequired
};
